import { BookingRecordType, InvoiceRecordType } from "pages/Booking/Booking.types";
import { OrderItemRecordType, OrderRecordType } from "pages/Order/Order.types";
import { EmailContactInfoTypeEnum, EmailRecipientRecordType } from "pages/SharedRecords.types";
import { useMemo } from "react";
import { useGetList, useTranslate } from "react-admin";

import { RecipientOptionType, RecipientSubjectType, TranslateFnType } from "./SendEmailDialog.types";

/**
 * Recipient options: one "who gets this, at which address" choice of the send dialog.
 * An option is not a single recipient - it expands over the whole selection, so an
 * option over 40 bookings carries 40 recipient rows. Several options can be active at
 * once; the dialog sends their union as one email job.
 *
 * Party availability is derived here in the frontend from the records the call site
 * already holds (an order summary tells us whether a buying person / organization
 * exists). A party option only covers the subjects that actually have that party, so
 * mixed selections do not produce dead recipients.
 */

const CONTACT_TYPES = [
  { type: EmailContactInfoTypeEnum.COMMUNICATION, labelKey: "emailing.contact_type.communication" },
  { type: EmailContactInfoTypeEnum.INVOICE, labelKey: "emailing.contact_type.invoice" },
];

// The backend resolves the party from this role (see ResolvesOrderRecipients). No role
// means the recipient's own default: the attendee of a booking, or - for an invoice -
// the billed party the backend derives per invoice.
export const RECIPIENT_ROLES = {
  BUYING_PERSON: "buying_person",
  ORGANIZATION: "organization",
};

/**
 * The minimum a booking has to expose for its parties to be derivable. The index
 * signature keeps records whose meta carries no order at all (lecturer bookings)
 * assignable - they simply offer no party options.
 */
export type BookingRecipientSubjectType = {
  id: number;
  meta?: { order?: { user: number | null; organization: number | null } | null; [key: string]: unknown };
};

export type BookingRecipientOptionsConfigType<T extends BookingRecipientSubjectType = BookingRecipientSubjectType> = {
  event?: number;
  /**
   * Overrides the recipient row, e.g. to attach a generated document per booking.
   */
  baseRecipient?: (booking: T) => EmailRecipientRecordType;
};

type PartyType = {
  id: string;
  role: string | null;
  label: string;
  subjects: RecipientSubjectType[];
};

function optionsForParties(parties: PartyType[], translate: TranslateFnType): RecipientOptionType[] {
  const withSubjects = parties.filter((party) => party.subjects.length > 0);
  // With a single party the party name adds nothing - the options are then just the two
  // address kinds, which is what the dialog offered before parties existed.
  const nameTheParty = withSubjects.length > 1;

  return withSubjects.flatMap((party) =>
    CONTACT_TYPES.map(({ type, labelKey }) => ({
      id: `${party.id}:${type}`,
      role: party.role,
      contactInfoType: type,
      label: nameTheParty ? `${party.label} · ${translate(labelKey)}` : translate(labelKey),
      recipients: party.subjects.map((subject) => ({
        ...subject.recipient,
        recipient_role: party.role,
        contact_info_type: type,
      })),
    })),
  );
}

/**
 * The two address kinds for a recipient set without commercial parties (attendees,
 * lecturers, users, organizations, interests, lists).
 */
export function buildContactTypeOptions(
  recipients: EmailRecipientRecordType[],
  translate: TranslateFnType,
): RecipientOptionType[] {
  return optionsForParties(
    [
      {
        id: "default",
        role: null,
        label: translate("emailing.parties.recipient"),
        subjects: recipients.map((recipient) => ({ recipient })),
      },
    ],
    translate,
  );
}

/** Options over subjects that hang on an order: their own default party plus the buyer. */
function buildPartyOptions(
  subjects: RecipientSubjectType[],
  translate: TranslateFnType,
  defaultPartyLabel: string | null,
): RecipientOptionType[] {
  const parties: PartyType[] = defaultPartyLabel
    ? [{ id: "default", role: null, label: defaultPartyLabel, subjects }]
    : [];

  return optionsForParties(
    parties.concat([
      {
        id: RECIPIENT_ROLES.BUYING_PERSON,
        role: RECIPIENT_ROLES.BUYING_PERSON,
        // Every order has a responsible person, so its presence is enough - the
        // summary's nullable ``user`` must not decide whether the option exists.
        label: translate("resources.orders.roles.buying_person"),
        subjects: subjects.filter((subject) => subject.hasOrder ?? !!subject.order),
      },
      {
        id: RECIPIENT_ROLES.ORGANIZATION,
        role: RECIPIENT_ROLES.ORGANIZATION,
        label: translate("resources.orders.roles.organization"),
        subjects: subjects.filter((subject) => subject.hasOrganization ?? !!subject.order?.organization),
      },
    ]),
    translate,
  );
}

export function buildOrderRecipientOptions(
  orders: OrderRecordType[],
  translate: TranslateFnType,
): RecipientOptionType[] {
  // An order has no recipient of its own - it is always one of the commercial parties.
  return buildPartyOptions(
    orders.map((order) => ({
      recipient: { related_resource: "order", related_id: order.id },
      order,
    })),
    translate,
    null,
  );
}

/**
 * Party options over order items. Recipients stay per item so the mail can talk about
 * the line at hand (``order_item`` placeholders) - a buyer with several selected items
 * therefore gets one mail per item, which the duplicate-recipient alert points out.
 */
export function buildOrderItemRecipientOptions(
  items: OrderItemRecordType[],
  translate: TranslateFnType,
): RecipientOptionType[] {
  return buildPartyOptions(
    items.map((item) => ({
      recipient: { related_resource: "order_item", related_id: item.id },
      // An item always belongs to an order; its parties are only known by name here.
      hasOrder: true,
      hasOrganization: !!item.meta?.organization_name,
    })),
    translate,
    null,
  );
}

export function buildInvoiceRecipientOptions(
  invoices: InvoiceRecordType[],
  translate: TranslateFnType,
  baseRecipient: (invoice: InvoiceRecordType) => EmailRecipientRecordType,
): RecipientOptionType[] {
  // Without a role the backend picks the billed party per invoice (organization for an
  // organization invoice, else the buying person) - the right default for a mixed
  // selection, which no single party option could express.
  return buildPartyOptions(
    invoices.map((invoice) => ({
      recipient: baseRecipient(invoice),
      order: invoice.meta?.order,
    })),
    translate,
    translate("emailing.parties.invoice_recipient"),
  );
}

export function buildBookingRecipientOptions<T extends BookingRecipientSubjectType>(
  bookings: T[],
  translate: TranslateFnType,
  options?: BookingRecipientOptionsConfigType<T>,
): RecipientOptionType[] {
  const baseRecipient =
    options?.baseRecipient ??
    ((booking: T) => ({
      related_resource: "booking" as const,
      related_id: booking.id,
      event: options?.event,
    }));

  return buildPartyOptions(
    bookings.map((booking) => ({ recipient: baseRecipient(booking), order: booking.meta?.order })),
    translate,
    translate("emailing.parties.attendee"),
  );
}

/**
 * Booking options for dialogs that know their bookings only as ids (post-processing,
 * post-cancel, after manual booking creation): the records are fetched to find out
 * which bookings have a buying person / organization. Call sites that already hold the
 * records pass ``bookings`` instead and no request is made.
 */
export function useBookingRecipientOptions<T extends BookingRecipientSubjectType>(
  params: { bookingIds?: number[]; bookings?: T[] } & BookingRecipientOptionsConfigType<T>,
): RecipientOptionType[] {
  const { bookings, event, baseRecipient } = params;
  const bookingIds = params.bookingIds ?? bookings?.map((booking) => booking.id) ?? [];
  const translate = useTranslate();
  // One list request instead of getMany, which issues a request per id.
  const { data: fetched } = useGetList<BookingRecordType>(
    "bookings",
    {
      filter: { id__in: bookingIds },
      pagination: { page: 1, perPage: Math.max(bookingIds.length, 1) },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: !bookings && bookingIds.length > 0 },
  );
  const records = (bookings ?? fetched) as T[] | undefined;

  // baseRecipient is rebuilt on every render by its call sites, so it is deliberately
  // not a dependency - the records and the event are what change the outcome.
  return useMemo(
    () => (records?.length ? buildBookingRecipientOptions(records, translate, { event, baseRecipient }) : []),
    [records, translate, event],
  );
}
